import { useState, useRef, useEffect } from 'react'
import iconCalendar from '../../assets/icons/calendar.svg'
import Button from '../Button'
import {
  getCurrentSeasonStartYear,
  getSeasonDateRange,
  matchSeasonFromRange,
} from '../../utils/seasonDateRange'
import './styles.css'

function DateRangePicker({ startDate, endDate, onChange }) {
  const [isOpen, setIsOpen] = useState(false)
  const [localStartDate, setLocalStartDate] = useState(startDate || '17/09/2024')
  const [localEndDate, setLocalEndDate] = useState(endDate || '20/10/2025')
  const pickerRef = useRef(null)

  const formatDate = (dateString) => {
    if (!dateString) return ''
    if (dateString.includes('/')) return dateString // Ya está formateado
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  const parseDate = (dateString) => {
    if (!dateString) return ''
    if (dateString.includes('-')) return dateString // Ya está en formato ISO
    const [day, month, year] = dateString.split('/')
    if (!day || !month || !year) return ''
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    if (startDate) {
      setLocalStartDate(formatDate(startDate))
    }
    if (endDate) {
      setLocalEndDate(formatDate(endDate))
    }
  }, [startDate, endDate])

  const handleStartDateChange = (e) => {
    const value = e.target.value
    const formatted = formatDate(value)
    setLocalStartDate(formatted)
  }

  const handleEndDateChange = (e) => {
    const value = e.target.value
    const formatted = formatDate(value)
    setLocalEndDate(formatted)
  }

  const handleFilter = () => {
    const parsedStartDate = parseDate(localStartDate) || startDate
    const parsedEndDate = parseDate(localEndDate) || endDate
    if (onChange) {
      onChange({ startDate: parsedStartDate, endDate: parsedEndDate })
    }
    setIsOpen(false)
  }

  return (
    <div className="date-range-picker-wrapper" ref={pickerRef}>
      <div 
        className={`date-range-picker ${isOpen ? 'date-range-picker--open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="date-input">
          <span className="date-text">{localStartDate}</span>
          <img src={iconCalendar} alt="" className="date-icon" />
        </div>
        <span className="date-separator">-</span>
        <div className="date-input">
          <span className="date-text">{localEndDate}</span>
          <img src={iconCalendar} alt="" className="date-icon" />
        </div>
      </div>
      {isOpen && (
        <div className="date-picker-calendar">
          <div className="date-picker-seasons">
            <label className="date-picker-label">Temporada cinegética</label>
            <div className="date-picker-seasons-list">
              {(() => {
                const current = getCurrentSeasonStartYear()
                const years = [current - 1, current]
                const activeYear = matchSeasonFromRange(
                  parseDate(localStartDate),
                  parseDate(localEndDate),
                )
                return years.map((year) => {
                  const isActive = year === activeYear
                  return (
                    <button
                      type="button"
                      key={year}
                      className={`date-picker-season-chip${isActive ? ' date-picker-season-chip--active' : ''}`}
                      onClick={() => {
                        const range = getSeasonDateRange(year)
                        setLocalStartDate(formatDate(range.startDate))
                        setLocalEndDate(formatDate(range.endDate))
                        if (onChange) onChange(range)
                        setIsOpen(false)
                      }}
                      title={`Del 01/04/${year} al 31/03/${year + 1}`}
                    >
                      {year}/{String(year + 1).slice(-2)}
                    </button>
                  )
                })
              })()}
            </div>
          </div>
          <div className="date-picker-separator" />
          <div className="date-picker-inputs">
            <div className="date-picker-field">
              <label className="date-picker-label">Desde</label>
              <input
                type="date"
                value={parseDate(localStartDate) || ''}
                onChange={handleStartDateChange}
                className="date-picker-input"
              />
            </div>
            <div className="date-picker-field">
              <label className="date-picker-label">Hasta</label>
              <input
                type="date"
                value={parseDate(localEndDate) || ''}
                onChange={handleEndDateChange}
                className="date-picker-input"
              />
            </div>
          </div>
          <div className="date-picker-button">
            <Button variant="primary" onClick={handleFilter}>
              Filtrar
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default DateRangePicker
